import {CityOption, Doctor, FormValues} from '@/type';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import {Controller, useForm} from 'react-hook-form';
import CitySelect from '../city/CitySelect';
import CalendarMobileView, {
  CalendarApiService
} from '../calendar/CalendarMobileView';
import CalendarDesktopView from '../calendar/CalendarDesktopView';
import Address from '../address/Address';
import DoctorsSelectMobile from '../doctors/DoctorsSelectMobile';
import DoctorsSelect from '../doctors/DoctorsSelect';
import {API_ROUTE, HOST} from '../../config';

import styles from './Postponement.module.scss';

export default function Postponement() {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order_id');
  const hash = params.get('hash');
  const status = params.get('status');

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {isSubmitting}
  } = useForm<FormValues>({
    defaultValues: {
      selected_city: {id: '1', name_city: 'Warszawa', office_id: '49, 31, 45'},
      selected_doctor: {id: '', name: ''},
      selected_date: new Date(),
      selected_time: {time: '', address: '', id: '', desc: ''}
    }
  });
  const [productId, setProductId] = useState<number>();
  const [citiesOption, setCitiesOption] = useState<CityOption[]>([]);
  const [addressOption, setAddressOption] = useState<CityOption[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [schedule, setSchedule] = useState<CalendarApiService[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasFetchedInit = useRef(false);
  const hasFetchedDoctors = useRef(false);
  const hasSetInitialDate = useRef(false);
  const refreshIntervalRef = useRef<number | null>(null);
  const [eligibility, setEligibility] = useState<{
    code: number;
    message: string;
  } | null>(null);
  
  

  const [slotError, setSlotError] = useState<string | null>(null);
  // Зберігаємо початкового лікаря для визначення логіки відображення
  const [initialDoctor, setInitialDoctor] = useState<{id: string; name: string} | null>(null);

  const selectedCity = watch('selected_city');
  const selectedDoctor = watch('selected_doctor');
  const selectedDate = watch('selected_date');
  const selectedTime = watch('selected_time');

  const availableDoctorIds = useMemo(() => {
    try {
      if (!selectedCity?.office_id || !Array.isArray(schedule)) {
        return new Set<string>();
      }
      const officeIds = String(selectedCity.office_id)
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter(Boolean);
      const ids = new Set<string>();
      for (const day of schedule) {
        if (!day?.Rows) continue;
        for (const row of day.Rows) {
          if (officeIds.includes(row.OfficeId)) {
            ids.add(String(row.DoctorId));
          }
        }
      }

      return ids;
    } catch (e) {
      return new Set<string>();
    }
  }, [schedule, selectedCity]);



  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  function getSchedule(pid?: number) {
    const p = pid || productId;
    if (!p) return;
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 39);
    const startDateStr = formatDate(today);
    const endDateStr = formatDate(endDate);

    const doctorParam =
      selectedDoctor?.name &&
      selectedDoctor.name !== 'Dowolny lekarz' &&
      selectedDoctor.id
        ? `&doctorID=${encodeURIComponent(String(selectedDoctor.id))}`
        : '';
    axios
      .get<{status: string; data: CalendarApiService[]}>(
        `${API_ROUTE}/get_list_schedule?date_start=${startDateStr}&date_end=${endDateStr}&id=${p}${doctorParam}`
      )
      .then((res) => {
        setSchedule(res.data.data);
      })
      .catch((err) => {
        console.error('Error fetching schedule:', err);
      });
  }

  const clearScheduleRefreshInterval = () => {
    if (refreshIntervalRef.current !== null) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const startScheduleRefreshInterval = () => {
    clearScheduleRefreshInterval();
    refreshIntervalRef.current = window.setInterval(() => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible'
      ) {
        getSchedule();
      }
    }, 5 * 60 * 1000);
  };

  useEffect(() => {
    // Init data from backend using order_id + hash
    if (hasFetchedInit.current) return;
    hasFetchedInit.current = true;
    if (!orderId || !hash) {
      setIsLoading(false);
      return;
    }

    axios
      .get(`${API_ROUTE}/postponement_init`, {
        params: {order_id: orderId, hash}
      })
      .then((res) => {
        const data = res.data;
        if (data?.product_id) setProductId(parseInt(data.product_id, 10));
        if (Array.isArray(data?.city_options))
          setCitiesOption(data.city_options);
        if (Array.isArray(data?.address_options))
          setAddressOption(data.address_options);
        if (data?.eligibility) setEligibility(data.eligibility);
        // Встановлюємо лікаря з даних або за замовчуванням
        if (data?.doctor) {
          const docId = String(data.doctor.id || '');
          const docName = String(data.doctor.name || 'Dowolny lekarz');
          const doctor = {id: docId, name: docName};
          const defaultDoctor = {id: '', name: 'Dowolny lekarz'};
          setValue('selected_doctor', defaultDoctor);
          setInitialDoctor(doctor);
        } else {
          // За замовчуванням встановлюємо "Dowolny lekarz"
          const defaultDoctor = {id: '', name: 'Dowolny lekarz'};
          setValue('selected_doctor', defaultDoctor);
          setInitialDoctor(defaultDoctor);
        }
      })
      .catch((err) => {
        console.error('Init error:', err);
        // Pokazujemy ten sam komunikat, co w legacy, gdy link/order nieprawidłowy
        setEligibility({code: 4, message: 'Ten porządek nie istnieje.'});
      })
      .finally(() => setIsLoading(false));
  }, [orderId, hash]);

  useEffect(() => {
    // Fetch doctors once
    if (hasFetchedDoctors.current) return;
    hasFetchedDoctors.current = true;
    axios
      .get(`${API_ROUTE}/get_doctor`, {
        withCredentials: true,
        headers: {'Content-Type': 'application/json'}
      })
      .then((res) => setDoctors(res.data.data_doctors))
      .catch((err) => console.error('Error fetching doctors', err));
  }, []);

  useEffect(() => {
    if (productId) {
      getSchedule(productId);
    }
  }, [productId]);

  // City from URL (?city=NAME)
  useEffect(() => {
    if (!Array.isArray(citiesOption) || citiesOption.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const cityParam = params.get('city');
      if (!cityParam) return;
      const normalized = decodeURIComponent(cityParam).trim().toLowerCase();
      const foundCity = citiesOption.find(
        (city) =>
          String((city as any)?.name_city || '')
            .trim()
            .toLowerCase() === normalized
      );
      if (foundCity) {
        setValue('selected_city', {
          id: String((foundCity as any).id),
          name_city: (foundCity as any).name_city,
          office_id: String((foundCity as any).office_id)
        });
      }
    } catch {}
  }, [citiesOption, setValue]);

  // Auto-select earliest available date when city+schedule present
  useEffect(() => {
    if (
      schedule &&
      Array.isArray(schedule) &&
      schedule.length > 0 &&
      selectedCity &&
      selectedCity.office_id &&
      !hasSetInitialDate.current
    ) {
      try {
        const now = new Date();
        const availableDates = schedule
          .filter((item) => {
            const itemDate = new Date(item.Date);
            const isToday =
              itemDate.getFullYear() === now.getFullYear() &&
              itemDate.getMonth() === now.getMonth() &&
              itemDate.getDate() === now.getDate();

            if (!item.Rows) return false;

            const hasValidSlots = item.Rows.some((row) => {
              const officeMatch = String(selectedCity.office_id).includes(
                String(row.OfficeId)
              );
              const doctorMatch =
                !selectedDoctor.id ||
                selectedDoctor.id === '' ||
                selectedDoctor.name === 'Dowolny lekarz' ||
                String(row.DoctorId) === selectedDoctor.id;
              const time = new Date(row.Time);
              const timeValid = !isToday || time > now;
              return officeMatch && doctorMatch && timeValid;
            });

            return hasValidSlots;
          })
          .map((item) => item.Date)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        if (availableDates.length > 0) {
          const firstAvailableDate = new Date(availableDates[0]);
          hasSetInitialDate.current = true;
          setValue('selected_date', firstAvailableDate);
        }
      } catch (error) {
        console.error('Error in auto-select date:', error);
      }
    }
  }, [schedule, selectedCity, selectedDoctor, setValue]);

  // При зміні міста встановлюємо "Dowolny lekarz" та скидаємо початкову дату
  useEffect(() => {
    // Якщо показується компонент вибору лікаря, то при зміні міста встановлюємо "Dowolny lekarz"
    if (initialDoctor && initialDoctor.name !== 'Dowolny lekarz') {
      setValue('selected_doctor', {id: '', name: 'Dowolny lekarz'});
    }
    hasSetInitialDate.current = false;
  }, [selectedCity?.id, initialDoctor, setValue]);

  // Reset selected time when date/doctor/city changes
  useEffect(() => {
    setValue('selected_time', {
      time: '',
      address: '',
      id: '',
      idsSameName: '',
      officeSameNameDoctors: '',
      desc: ''
    });
  }, [selectedDate, selectedDoctor, selectedCity, setValue]);

  const onSubmit = async (data: any) => {
    try {
      if (!orderId || !hash) return;
      if (eligibility && eligibility.code !== 1) return;

      // Format date
      let selectedDateFormatted;
      if (data.selected_date instanceof Date) {
        const year = data.selected_date.getFullYear();
        const month = String(data.selected_date.getMonth() + 1).padStart(
          2,
          '0'
        );
        const day = String(data.selected_date.getDate()).padStart(2, '0');
        selectedDateFormatted = `${year}-${month}-${day}`;
      } else {
        selectedDateFormatted = data.selected_date;
      }

      const payload = {
        order_id: orderId,
        hash,
        postponement_date: selectedDateFormatted,
        postponement_time: data.selected_time.time,
        postponement_office: data.selected_time.id,
        selected_cabinet_name: data.selected_time.address
      };

      const headers: any = {'Content-Type': 'application/json'};
      const wpNonce = (window as any).postponementFormVars?.nonce;
      if (wpNonce) headers['X-WP-Nonce'] = wpNonce;

      const response = await axios.post(
        `${API_ROUTE}/postponement_update`,
        payload,
        {headers}
      );
      if (response?.data?.success) {
        window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=success`;
      } else {
        window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=notsuccess`;
      }
    } catch (err) {
      console.error('Postponement error:', err);
      window.location.href = `${HOST}/postponement-thanks/?order_id=${orderId}&status=notsuccess`;
    }
  };

  console.log('selectedDoctor:', doctors);
  console.log('availableDoctorIds:', availableDoctorIds);
  
  return (
    <div>
      {isLoading && (
        <div
          className='loading-indicator'
          style={{marginBottom: 16, marginTop: "10%"}}
        >
          <div className='spinner'></div>
          <p>Ładowanie danych…</p>
        </div>
      )}

      {/* Jeśli błąd – pokazуємо лише повідомлення (forma ховається) */}
      {!isLoading && eligibility && eligibility.code !== 1 && (
        <div className={styles.container}>
          <div className={styles.errorMessages}>
            {eligibility.message || 'Zmiana terminu niedozwolona.'}
          </div>
        </div>
      )}

      {/* Форму показуємо лише якщо дозволено або eligibility ще не визначена */}
      {!isLoading && (!eligibility || eligibility.code === 1) && (
        <div className={styles.container}>
          <h1 className={styles.postponementTitle}>
            <span>Zmiana</span> terminu wizyty
          </h1>
          <p className={styles.postponementSubtitle}>
            Jeśli chcesz zmienić termin wizyty, skorzystaj z poniższego
            formularza
          </p>
          <div className={styles.postponementWarning}>
            <span>UWAGA!</span> Tę wizytę można przełożyć tylko raz. Po zmianie
            terminu otrzymają Państwo potwierdzenie e-mail. Każda kolejna
            rejestracja również będzie miała możliwość jednorazowego przełożenia
            wizyty.{' '}
          </div>
          <form
            className='r-form-single-product'
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className='step-container'>
              <Controller
                name='selected_city'
                control={control}
                render={({field}) => (
                  <CitySelect {...field} citiesOption={citiesOption} />
                )}
              />

              <div className='r-form-fields-container'>
                {/* Показуємо вибір лікаря тільки якщо початковий лікар не був "Dowolny lekarz" */}
                {initialDoctor && initialDoctor.name !== 'Dowolny lekarz' && (
                  <Controller
                    name='selected_doctor'
                    control={control}
                    render={({field}) => (
                      <>
                        <div className='doctor-mobile'>
                          <DoctorsSelectMobile
                            value={field.value}
                            onChange={field.onChange}
                            officeId={selectedCity?.office_id}
                            doctors={doctors}
                            availableDoctorIds={availableDoctorIds}
                          />
                        </div>
                        <div className='doctor-desktop'>
                          <DoctorsSelect
                            value={field.value}
                            onChange={field.onChange}
                            officeId={selectedCity?.office_id}
                            doctors={doctors}
                            availableDoctorIds={availableDoctorIds}
                          />
                        </div>
                      </>
                    )}
                  />
                )}

                <Controller
                  name='selected_date'
                  control={control}
                  render={({field}) => (
                    <>
                      <div className='calendar-mobile'>
                        <CalendarMobileView
                          {...field}
                          productId={productId}
                          officeId={selectedCity?.office_id}
                          doctorId={selectedDoctor?.id}
                          schedule={schedule}
                        />
                      </div>
                      <div className='calendar-desktop'>
                        <CalendarDesktopView
                          {...field}
                          productId={productId}
                          officeId={selectedCity?.office_id}
                          doctorId={selectedDoctor?.id}
                          schedule={schedule}
                        />
                      </div>
                    </>
                  )}
                />

                <Controller
                  name='selected_time'
                  control={control}
                  render={({field}) => {
                    let filteredSchedule = schedule;

                    if (selectedCity?.office_id && Array.isArray(schedule)) {
                      const officeIds = selectedCity.office_id
                        .split(',')
                        .map((id) => parseInt(id.trim(), 10))
                        .filter(Boolean);

                      filteredSchedule = schedule.filter((item) => {
                        const hasMatchingRows = item.Rows.some((row) => {
                          const officeMatch = officeIds.includes(row.OfficeId);
                          // Якщо початковий лікар був "Dowolny lekarz", показуємо всіх лікарів
                          // Якщо був конкретний лікар, дозволяємо вибирати інших лікарів
                          const doctorMatch =
                            !selectedDoctor?.id ||
                            selectedDoctor.id === '' ||
                            selectedDoctor.name === 'Dowolny lekarz' ||
                            String(row.DoctorId) === String(selectedDoctor.id);

                          return officeMatch && doctorMatch;
                        });

                        return hasMatchingRows;
                      });
                    }

                    return (
                      <>
                        <Address
                          {...field}
                          productId={productId}
                          officeIds={selectedCity?.office_id}
                          doctorId={selectedDoctor?.id}
                          addressOption={addressOption}
                          schedule={filteredSchedule}
                          selectedDate={selectedDate}
                          doctors={doctors}
                          onChange={field.onChange}
                        />
                        {slotError && (
                          <div
                            style={{
                              color: 'red',
                              marginTop: 8,
                              fontSize: 14
                            }}
                          >
                            {slotError}
                          </div>
                        )}
                      </>
                    );
                  }}
                />

                <button
                  type='submit'
                  className={styles['submit-button']}
                  disabled={
                    !selectedCity?.name_city ||
                    !selectedTime?.time ||
                    isSubmitting ||
                    Boolean(eligibility && eligibility.code !== 1)
                  }
                >
                  Zatwierdź nowy termin
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
